import { mapPlayerLook, mapRewardBonus } from '@/lib/api/rewards-hub/mappers';
import type { RewardBonus } from '@/lib/api/rewards-hub/models';
import {
  DEFAULT_LEADERBOARD_DIRECTION,
  LEADERBOARD_METRICS,
  type LeaderboardEntry,
  type LeaderboardMetric,
  type LeaderboardPeriod,
  type LeaderboardSegment,
  type LeaderboardSortDirection,
  type LeaderboardWinner,
  type RewardsLeaderboard,
} from './models';
import type {
  LeaderboardBoardDto,
  LeaderboardDirectionDto,
  LeaderboardDto,
  LeaderboardMetricDto,
  LeaderboardPeriodDto,
  LeaderboardRowDto,
} from './types';

const METRIC_BY_DTO: Record<LeaderboardMetricDto, LeaderboardMetric> = {
  VOLUME: 'volume',
  TRADES: 'trades',
  BEST_DEAL: 'bestDeal',
  BEST_ROE: 'roe',
  TASKS: 'tasks',
};

const DTO_BY_METRIC: Record<LeaderboardMetric, LeaderboardMetricDto> = {
  volume: 'VOLUME',
  trades: 'TRADES',
  bestDeal: 'BEST_DEAL',
  roe: 'BEST_ROE',
  tasks: 'TASKS',
};

const SEGMENT_BY_DTO: Record<LeaderboardBoardDto, LeaderboardSegment> = {
  ALL: 'all',
  PREDICTION: 'prediction',
  CRYPTO: 'crypto',
};

const DTO_BY_SEGMENT: Record<LeaderboardSegment, LeaderboardBoardDto> = {
  all: 'ALL',
  prediction: 'PREDICTION',
  crypto: 'CRYPTO',
};

const PERIOD_BY_DTO: Record<LeaderboardPeriodDto, LeaderboardPeriod> = {
  ALL: 'all',
  DAY: 'day',
  MONTH: 'month',
  YEAR: 'year',
};

const DTO_BY_PERIOD: Record<LeaderboardPeriod, LeaderboardPeriodDto> = {
  all: 'ALL',
  day: 'DAY',
  month: 'MONTH',
  year: 'YEAR',
};

export function toBoardDto(segment: LeaderboardSegment): LeaderboardBoardDto {
  return DTO_BY_SEGMENT[segment];
}

export function toMetricDto(metric: LeaderboardMetric): LeaderboardMetricDto {
  return DTO_BY_METRIC[metric];
}

export function toPeriodDto(period: LeaderboardPeriod): LeaderboardPeriodDto {
  return DTO_BY_PERIOD[period];
}

const DIRECTION_BY_DTO: Record<LeaderboardDirectionDto, LeaderboardSortDirection> = {
  DESC: 'desc',
  ASC: 'asc',
};

const DTO_BY_DIRECTION: Record<LeaderboardSortDirection, LeaderboardDirectionDto> = {
  desc: 'DESC',
  asc: 'ASC',
};

export function toDirectionDto(direction: LeaderboardSortDirection): LeaderboardDirectionDto {
  return DTO_BY_DIRECTION[direction];
}

export function mapLeaderboardEntry(dto: LeaderboardRowDto, meId: string | null): LeaderboardEntry {
  return {
    userId: dto.u,
    username: dto.n?.trim() ? dto.n : null,
    look: mapPlayerLook(dto.lk),
    rankIndex: dto.rk ?? null,
    position: dto.p,
    volume: dto.vol ?? 0,
    trades: dto.tr ?? 0,
    bestDeal: dto.bd ?? null,
    roe: dto.roe ?? null,
    tasks: dto.tk ?? 0,
    artifacts: {
      achievements: dto.ach ?? 0,
      medals: dto.md ?? 0,
      cups: dto.cp ?? 0,
      diamonds: dto.dm ?? 0,
    },
    isMe: meId !== null && dto.u === meId,
  };
}

/**
 * Leaders in column order. A metric without one is left out — an empty board,
 * or Best Deal / Best ROE on a tab where nobody has closed a deal yet (29.09).
 */
function mapWinners(ld: LeaderboardDto['ld'] | undefined): LeaderboardWinner[] {
  return LEADERBOARD_METRICS.flatMap((metric) => {
    const row = ld?.[DTO_BY_METRIC[metric]];
    if (!row) return [];
    const entry = mapLeaderboardEntry(row, null);
    const value = entry[metric];
    if (value === null) return [];
    return [
      {
        metric,
        player: {
          userId: entry.userId,
          username: entry.username,
          look: entry.look,
          rankIndex: entry.rankIndex,
        },
        value,
      },
    ];
  });
}

function mapPrizes(pz: LeaderboardDto['pz']): RewardsLeaderboard['prizes'] {
  const prizes: Partial<Record<LeaderboardMetric, RewardBonus>> = {};
  for (const metric of LEADERBOARD_METRICS) {
    const bonus = mapRewardBonus(pz?.[DTO_BY_METRIC[metric]]);
    if (bonus) prizes[metric] = bonus;
  }
  return prizes;
}

/**
 * `requestedPeriod` stands in for `pr` when the service does not echo it (one
 * from before 07.10): without it the page would hold every answer to a period
 * switch as a late one and keep the table dimmed. No `o` is a service from
 * before 02.11, which only sorts best first.
 */
export function mapLeaderboard(
  dto: LeaderboardDto,
  requestedPeriod: LeaderboardPeriod = 'all',
): RewardsLeaderboard {
  // `me` is the same row that is in `rows` — it only says which one to mark.
  const meId = dto.me?.u ?? null;
  return {
    segment: SEGMENT_BY_DTO[dto.b] ?? 'all',
    metric: METRIC_BY_DTO[dto.m] ?? 'volume',
    direction: (dto.o && DIRECTION_BY_DTO[dto.o]) || DEFAULT_LEADERBOARD_DIRECTION,
    period: (dto.pr && PERIOD_BY_DTO[dto.pr]) || requestedPeriod,
    winners: mapWinners(dto.ld),
    prizes: mapPrizes(dto.pz),
    entries: (dto.rows ?? []).map((row) => mapLeaderboardEntry(row, meId)),
    page: dto.pg ?? 1,
    pageCount: dto.pgs ?? 1,
    hasStanding: meId !== null,
  };
}
